import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { PhoneInput } from '@column/column-ui-kit';
import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@column/ui';

import { PEP_STATUS_OPTIONS } from '../_util/entityOptions';
import type { PersonEntityFormValues } from '../_util/useEntityForm';
import { AddressField, DateInput, RenderFields } from '~/components';
import { SectionField, SectionWithFields } from '~/components/RenderFields';
import { useAddress } from '~/hooks';

import { NumberListInput, PaymentRailsInput, StringListInput, YesNoInput } from './EntityFieldInputs';
import { PersonIdentificationFields } from './PersonIdentificationFields';

// Seed the "Is this person a U.S. Citizen?" toggle: default new people to citizen, and for an
// existing person infer it from whichever identification document is already on file.
const useIsCitizenDefaults = (form: ReturnType<typeof useFormContext>, hasId: boolean) => {
  const isCitizen = form.watch('temporaryData.isCitizen');
  const ssn = form.watch('ssn');
  const passport = form.watch('passport.number');
  const driversLicense = form.watch('driversLicense.number');
  const nationalId = form.watch('nationalId.number');

  useEffect(() => {
    if (typeof isCitizen !== 'boolean') {
      if (!hasId) {
        form.setValue('temporaryData.isCitizen', true);
        return;
      }
      if (ssn) {
        form.setValue('temporaryData.isCitizen', true);
      } else if (passport || driversLicense || nationalId) {
        form.setValue('temporaryData.isCitizen', false);
      } else {
        form.setValue('temporaryData.isCitizen', true);
      }
    }
  }, [form, hasId, isCitizen, ssn, passport, driversLicense, nationalId]);
};

export interface PersonEntityFieldsProps {
  // The id of an existing person when editing; absent while creating. Drives the citizen default
  // and whether the SSN input starts revealed.
  id?: string;
  disableForm?: boolean;
  // The full person page shows contact fields (middle name, email, phone); the create-and-link
  // modal omits them to stay lean. The shared field definitions are otherwise identical.
  includeContactFields?: boolean;
  // Headline for the leading section (the page labels it "Information"; the modal renders it bare).
  informationHeadline?: string;
}

// The person-entity form fields, shared by the full Person Entity page and the create-and-link
// modal so their inputs, validation rules, and layout can never drift apart.
export const PersonEntityFields: React.FC<PersonEntityFieldsProps> = ({
  id,
  disableForm,
  includeContactFields,
  informationHeadline,
}) => {
  const form = useFormContext();
  const { countries, states } = useAddress({ types: ['state', 'country'] });
  const [addressSearchValue, setAddressSearchValue] = useState('');

  useIsCitizenDefaults(form, Boolean(id));

  const isCitizen = form.watch('temporaryData.isCitizen') === true;
  const requiresUSAddressRegion = form.watch('address.countryCode') === 'US';

  const countryOptions = countries.map((c) => ({ label: c.name, value: c.code }));

  const middleName: SectionField<PersonEntityFormValues, undefined> = {
    id: 'middleName',
    label: 'Middle Name',
    children: ({ value, onChange, onBlur }) => (
      <Input
        className="w-full"
        value={(value as string) ?? ''}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="Middle Name"
        disabled={disableForm}
      />
    ),
  };

  const contactFields: SectionField<PersonEntityFormValues, undefined>[] = [
    {
      id: 'email',
      label: 'Email Address',
      children: ({ value, onChange, onBlur }) => (
        <Input
          className="w-full"
          value={(value as string) ?? ''}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Email Address"
          disabled={disableForm}
        />
      ),
    },
    {
      id: 'phoneNumber',
      label: 'Phone Number',
      children: ({ value, onChange }) => (
        <PhoneInput
          value={(value as string) ?? ''}
          onChange={onChange}
          countryList={countries}
          isDisabled={disableForm}
          placeholder=""
        />
      ),
    },
  ];

  const sections: SectionWithFields<PersonEntityFormValues, undefined>[] = [
    {
      headline: informationHeadline,
      fields: [
        {
          id: 'firstName',
          label: 'First Name',
          rules: { required: true },
          children: ({ value, onChange, onBlur }, { isTouched, error }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="First Name"
              disabled={disableForm}
              aria-invalid={isTouched && !!error ? true : undefined}
            />
          ),
        },
        ...(includeContactFields ? [middleName] : []),
        {
          id: 'lastName',
          label: 'Last Name',
          rules: { required: true },
          children: ({ value, onChange, onBlur }, { isTouched, error }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Last Name"
              disabled={disableForm}
              aria-invalid={isTouched && !!error ? true : undefined}
            />
          ),
        },
        {
          id: 'dateOfBirth',
          label: 'Date of Birth',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <DateInput value={(value as string) ?? ''} onChange={onChange} isDisabled={disableForm} />
          ),
        },
        ...(includeContactFields ? contactFields : []),
        {
          id: 'temporaryData.isCitizen',
          label: 'Is this person a U.S. Citizen?',
          children: ({ value, onChange }) => <YesNoInput value={value} onChange={onChange} isDisabled={disableForm} />,
        },
        {
          content: (
            <PersonIdentificationFields
              isCitizen={isCitizen}
              countryOptions={countryOptions}
              disabled={disableForm}
              ssnDefaultVisible={!id}
            />
          ),
        },
      ],
    },
    {
      headline: 'Address',
      fields: [
        {
          id: 'address',
          label: 'Search Address',
          children: () => (
            <AddressField
              onChange={(a) => {
                form.setValue('address.line1', a.line1);
                form.setValue('address.line2', a.line2);
                form.setValue('address.city', a.city);
                form.setValue('address.state', a.state);
                form.setValue('address.postalCode', a.postalCode);
                form.setValue('address.countryCode', a.countryCode);
                setAddressSearchValue('');
              }}
              justUS
              isDisabled={disableForm}
              value={addressSearchValue}
              onValueChange={setAddressSearchValue}
            />
          ),
        },
        {
          id: 'address.line1',
          label: 'Line 1',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Line 1"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'address.line2',
          label: 'Line 2',
          children: ({ value, onChange }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Line 2"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'address.city',
          label: 'City',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="City"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'address.state',
          label: 'State',
          rules: requiresUSAddressRegion ? { required: true } : undefined,
          children: ({ value, onChange }) => {
            const countryCode = form.getValues('address.countryCode');
            // Default the state options to the U.S. until a country is chosen.
            const filteredStates = states.filter((s) => s.country_code === (countryCode || 'US'));
            return (
              <Select value={(value as string) ?? ''} onValueChange={onChange} disabled={disableForm}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select State..." />
                </SelectTrigger>
                <SelectContent>
                  {filteredStates.map((s) => (
                    <SelectItem key={s.state_code} value={s.state_code}>
                      {s.name} ({s.state_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          },
        },
        {
          id: 'address.postalCode',
          label: 'Postal Code',
          rules: requiresUSAddressRegion ? { required: true } : undefined,
          children: ({ value, onChange }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Postal Code"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'address.countryCode',
          label: 'Country',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Select value={(value as string) ?? ''} onValueChange={onChange} disabled={disableForm}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Country..." />
              </SelectTrigger>
              <SelectContent>
                {countryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
        },
      ],
    },
    {
      headline: 'Employment & Income',
      isCollapsed: true,
      fields: [
        {
          id: 'employmentStatus',
          label: 'Employment Status',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Employment Status"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'occupation',
          label: 'Occupation',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Occupation"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'role',
          label: 'Role',
          description: "Individual's role or job title at their place of employment.",
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Role"
              disabled={disableForm}
            />
          ),
        },
        {
          id: 'sourceOfIncome',
          label: 'Source of Income',
          description: 'Comma-separated list.',
          children: ({ value, onChange }) => (
            <StringListInput
              value={value as string[] | undefined}
              onChange={onChange}
              placeholder="e.g. salary, investments"
              isDisabled={disableForm}
            />
          ),
        },
        {
          id: 'income',
          label: 'Estimated Annual Income',
          description: 'Comma-separated amounts to pass income ranges.',
          children: ({ value, onChange }) => (
            <NumberListInput
              value={value as number[] | undefined}
              onChange={onChange}
              placeholder="e.g. 50000, 100000"
              isDisabled={disableForm}
            />
          ),
        },
      ],
    },
    {
      headline: 'Account Activity',
      isCollapsed: true,
      fields: [
        {
          id: 'purposeOfAccount',
          label: 'Purpose of Account',
          description: 'Comma-separated list.',
          children: ({ value, onChange }) => (
            <StringListInput
              value={value as string[] | undefined}
              onChange={onChange}
              placeholder="Intended use of the account"
              isDisabled={disableForm}
            />
          ),
        },
        {
          id: 'expectedPaymentRails',
          label: 'Expected Payment Rails',
          children: ({ value, onChange }) => (
            <PaymentRailsInput value={value} onChange={onChange} isDisabled={disableForm} />
          ),
        },
        {
          id: 'makesInternationalPayments',
          label: 'Makes International Payments?',
          children: ({ value, onChange }) => <YesNoInput value={value} onChange={onChange} isDisabled={disableForm} />,
        },
      ],
    },
    {
      headline: 'Compliance',
      isCollapsed: true,
      fields: [
        {
          id: 'countryOfCitizenship',
          label: 'Country of Citizenship',
          children: ({ value, onChange }) => (
            <Select value={(value as string) ?? ''} onValueChange={onChange} disabled={disableForm}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Country..." />
              </SelectTrigger>
              <SelectContent>
                {countryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
        },
        {
          id: 'pepStatus',
          label: 'PEP Status',
          description: 'Politically Exposed Person screening status.',
          children: ({ value, onChange }) => (
            <Select value={(value as string) ?? ''} onValueChange={onChange} disabled={disableForm}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select PEP Status..." />
              </SelectTrigger>
              <SelectContent>
                {PEP_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
        },
        {
          id: 'isHighRisk',
          label: 'High Risk?',
          children: ({ value, onChange }) => <YesNoInput value={value} onChange={onChange} isDisabled={disableForm} />,
        },
      ],
    },
  ];

  return <RenderFields sections={sections} />;
};
